// ---------------------------------------------------------------------------
// C10: Franchise Benchmarks — /admin/franchise/benchmarks
// ---------------------------------------------------------------------------
// Location performance table. Ranking. AI recommendations per underperformer.
// ---------------------------------------------------------------------------
import { useState } from "react";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Trophy,
  AlertTriangle,
  Sparkles,
  MapPin,
  Star,
  Calendar,
  DollarSign,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useFranchiseBenchmarks, type LocationBenchmark } from "@/hooks/use-franchise";
import { EmptyState } from "@/components/ui/empty-state";

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function BenchmarkSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend Indicator
// ---------------------------------------------------------------------------

function TrendIndicator({ trend }: { trend: "up" | "down" | "stable" }) {
  if (trend === "up") return <TrendingUp className="h-4 w-4 text-[var(--color-success)]/500" />;
  if (trend === "down") return <TrendingDown className="h-4 w-4 text-[var(--color-destructive)]" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

// ---------------------------------------------------------------------------
// Rank Badge
// ---------------------------------------------------------------------------

function RankBadge({ rank, total }: { rank: number; total: number }) {
  if (rank === 1) return <Trophy className="h-5 w-5 text-[var(--color-warning)]" />;
  if (rank === 2) return <Trophy className="h-5 w-5 text-[var(--color-muted-foreground)]" />;
  if (rank === 3) return <Trophy className="h-5 w-5 text-[var(--color-warning)]" />;
  if (rank > total * 0.75) return <AlertTriangle className="h-4 w-4 text-[var(--color-destructive)]" />;
  return <span className="text-sm font-medium text-muted-foreground">#{rank}</span>;
}

// ---------------------------------------------------------------------------
// Location Row (expandable for AI recommendations)
// ---------------------------------------------------------------------------

function LocationRow({
  location,
  averages,
  totalLocations,
}: {
  location: LocationBenchmark;
  averages: { revenue: number; bookings: number; reviewsCount: number; averageRating: number; responseTimeHours: number };
  totalLocations: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const isUnderperformer = location.rank > totalLocations * 0.75;

  const compareClass = (value: number, avg: number, higher: boolean) => {
    if (higher ? value > avg : value < avg) return "text-[var(--color-success)]";
    if (higher ? value < avg * 0.8 : value > avg * 1.2) return "text-[var(--color-destructive)]";
    return "text-foreground";
  };

  return (
    <>
      <tr
        className={`border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${isUnderperformer ? "bg-[var(--color-destructive)]/30" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 pr-4 pl-4">
          <div className="flex items-center gap-2">
            <RankBadge rank={location.rank} total={totalLocations} />
          </div>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium text-foreground">{location.locationName}</span>
            <TrendIndicator trend={location.trend} />
          </div>
        </td>
        <td className={`py-3 px-4 text-sm font-medium ${compareClass(location.revenue, averages.revenue, true)}`}>
          €{location.revenue.toLocaleString()}
        </td>
        <td className={`py-3 px-4 text-sm ${compareClass(location.bookings, averages.bookings, true)}`}>
          {location.bookings}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-0.5">
            <Star className="h-3 w-3 fill-amber-400 text-[var(--color-warning)]/80" />
            <span className={`text-sm ${compareClass(location.averageRating, averages.averageRating, true)}`}>
              {location.averageRating.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground">({location.reviewsCount})</span>
          </div>
        </td>
        <td className={`py-3 px-4 text-sm ${compareClass(location.responseTimeHours, averages.responseTimeHours, false)}`}>
          {location.responseTimeHours}h
        </td>
        <td className="py-3 px-4 text-sm text-muted-foreground">
          {location.customerSatisfaction}%
        </td>
        <td className="py-3 pl-4 pr-4">
          {location.aiRecommendations.length > 0 && (
            expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </td>
      </tr>
      {expanded && location.aiRecommendations.length > 0 && (
        <tr className="bg-[var(--color-ai)]/5">
          <td colSpan={8} className="px-4 py-3">
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-[var(--color-ai)] mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-medium text-[var(--color-ai)]">AI-generated recommendations</span>
                <ul className="mt-1 space-y-1">
                  {location.aiRecommendations.map((rec, i) => (
                    <li key={i} className="text-sm text-foreground">• {rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function FranchiseBenchmarks() {
  const [period, setPeriod] = useState<string>("30d");
  const { data, isLoading, error } = useFranchiseBenchmarks(period);

  if (isLoading) return <BenchmarkSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load franchise benchmarks.
      </div>
    );
  }

  if (!data || data.locations.length === 0) {
    return (
      <EmptyState
        icon={<BarChart3 className="h-8 w-8" />}
        title="No franchise locations yet"
        description="Benchmarks will appear once you have multiple franchise locations reporting data."
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Franchise Benchmarks</h1>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {(["7d", "30d", "90d", "1y"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                period === p
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            Locations
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{data.locations.length}</div>
          <div className="text-xs text-muted-foreground">Active franchise locations</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Trophy className="h-4 w-4 text-[var(--color-warning)]" />
            Top Performer
          </div>
          <div className="mt-2 text-lg font-bold text-foreground truncate">{data.topPerformer}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            Avg Revenue
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">€{data.averages.revenue.toLocaleString()}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-[var(--color-destructive)]" />
            Underperformers
          </div>
          <div className="mt-2 text-2xl font-bold text-[var(--color-destructive)]">{data.underperformers.length}</div>
          <div className="text-xs text-muted-foreground">Need attention</div>
        </div>
      </div>

      {/* Benchmark Table */}
      <div className="rounded-lg border border-border bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="py-3 pr-4 pl-4 text-left font-medium text-muted-foreground w-16">Rank</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">Location</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">Revenue</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">Bookings</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">Rating</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">Response</th>
                <th className="py-3 px-4 text-left font-medium text-muted-foreground">CSAT</th>
                <th className="py-3 pl-4 pr-4 w-8" />
              </tr>
            </thead>
            <tbody>
              {[...data.locations]
                .sort((a, b) => a.rank - b.rank)
                .map((location) => (
                  <LocationRow
                    key={location.locationId}
                    location={location}
                    averages={data.averages}
                    totalLocations={data.locations.length}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Averages footer */}
      <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
        <span className="font-medium">Network Averages:</span>{" "}
        Revenue €{data.averages.revenue.toLocaleString()} | Bookings {data.averages.bookings} | Rating {data.averages.averageRating.toFixed(1)} | Response {data.averages.responseTimeHours}h
      </div>
    </div>
  );
}
